import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, Ban } from 'lucide-react';
import { Contact } from '../../../types/models/whatsapp';

interface DeleteContactDialogProps {
  contact: Contact | null;
  onClose: () => void;
  onDelete: (contactId: string) => Promise<{ detachedConversations: number }>;
  /** Offered as the safer alternative, since delete does not stop messages. */
  onBlock: (contact: Contact) => Promise<void>;
}

/**
 * Confirms deleting a contact.
 *
 * Deliberately not a one-click action: it is irreversible, and it is easy to
 * assume it also stops the contact being messaged. It does not — blocking is
 * what does that — so the alternative is offered here rather than left to be
 * discovered afterwards.
 */
const DeleteContactDialog: React.FC<DeleteContactDialogProps> = ({
  contact,
  onClose,
  onDelete,
  onBlock
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
  }, [contact]);

  if (!contact) return null;

  const handleDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      await onDelete(contact.id);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Could not delete this contact');
    } finally {
      setBusy(false);
    }
  };

  const handleBlock = async () => {
    setBusy(true);
    setError(null);
    try {
      await onBlock(contact);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Could not block this contact');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Delete contact</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 p-4">
          <p className="text-sm text-gray-700">
            Delete{' '}
            <strong className="text-gray-900">
              {contact.name || contact.phone}
            </strong>
            {contact.name && (
              <span className="text-gray-500"> ({contact.phone})</span>
            )}
            ?
          </p>

          <ul className="space-y-1 rounded-lg bg-gray-50 p-2.5 text-[11px] text-gray-600">
            <li>• The contact record is permanently removed.</li>
            <li>
              • Chat history is <strong>kept</strong> as a record of what was
              sent, shown under the phone number.
            </li>
            <li>
              • This does <strong>not</strong> stop future messages — if they
              message you again, a new contact is created.
            </li>
          </ul>

          {!contact.blocked && (
            <div className="rounded-lg border border-warning-500/30 bg-warning-50 p-2.5">
              <p className="flex items-start gap-1.5 text-[11px] text-black">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                To stop messaging this contact, block them instead. That is
                reversible and keeps the record intact.
              </p>
              <button
                type="button"
                onClick={handleBlock}
                disabled={busy}
                className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Ban className="h-3 w-3" />
                Block instead
              </button>
            </div>
          )}

          {error && <p className="text-xs text-danger-600">{error}</p>}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={busy}
              className="flex-1 rounded-lg bg-danger-600 px-3 py-2 text-sm font-medium text-white hover:bg-danger-500 disabled:opacity-50"
            >
              {busy ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteContactDialog;
